import { useState, useEffect, useCallback } from 'react';
import { getCreditBalance, getServiceCosts, deductCredits, checkAndGrantCredits } from '../services/api';
import SessionManager from '../utils/sessionManager';

/**
 * useCredits hook - Manages credit balance and service costs
 * 
 * @returns {Object} Credit state and functions
 */
const useCredits = () => {
  const [balance, setBalance] = useState(null);
  const [costs, setCosts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCredits = useCallback(async () => {
    try {
      const session = SessionManager.getSession('candidate');
      if (!session?.profile?.id) {
        setLoading(false);
        return;
      }

      // Check and grant any pending credits first
      await checkAndGrantCredits(session.profile.id).catch(() => {});

      // Fetch balance and costs
      const [balanceData, costsData] = await Promise.all([
        getCreditBalance(session.profile.id),
        getServiceCosts()
      ]);

      setBalance(balanceData);
      setCosts(costsData);
      setError(null);
    } catch (err) {
      console.error('Error fetching credits:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCredits();

    // Listen for credit updates
    const handleCreditUpdate = () => fetchCredits();
    window.addEventListener('creditBalanceUpdated', handleCreditUpdate);
    return () => window.removeEventListener('creditBalanceUpdated', handleCreditUpdate);
  }, [fetchCredits]);

  /**
   * Check if user has enough credits for a service
   * @param {string} serviceType - Service type key
   * @returns {boolean}
   */
  const hasEnoughCredits = useCallback((serviceType) => {
    if (!balance || !costs[serviceType]) return false;
    return balance.balance >= costs[serviceType];
  }, [balance, costs]);

  /**
   * Get cost for a specific service
   * @param {string} serviceType - Service type key
   * @returns {number}
   */
  const getCost = useCallback((serviceType) => {
    return costs[serviceType] || 0;
  }, [costs]);

  /**
   * Deduct credits for a service
   * @param {string} serviceType - Service type key
   * @param {string} referenceId - Optional reference ID
   * @returns {Promise<Object>} Result with success status
   */
  const deductCreditsForService = useCallback(async (serviceType, referenceId = null) => {
    try {
      const session = SessionManager.getSession('candidate');
      if (!session?.profile?.id) {
        throw new Error('Please log in to continue');
      }

      const result = await deductCredits(session.profile.id, serviceType, referenceId);
      
      // Update local balance
      setBalance(prev => ({
        ...prev,
        balance: result.new_balance,
        total_spent: (prev?.total_spent || 0) + result.credits_deducted
      }));

      // Notify other components
      window.dispatchEvent(new CustomEvent('creditBalanceUpdated'));

      return { success: true, ...result };
    } catch (err) {
      // Check if it's an insufficient credits error
      if (err.message?.includes('insufficient_credits') || err.message?.includes('402')) {
        return {
          success: false,
          error: 'insufficient_credits',
          message: err.message,
          required: costs[serviceType],
          available: balance?.balance || 0
        };
      }
      throw err;
    }
  }, [balance, costs]);

  /**
   * Refresh credit balance
   */
  const refreshBalance = useCallback(() => {
    fetchCredits();
  }, [fetchCredits]);

  return {
    balance: balance?.balance || 0,
    totalEarned: balance?.total_earned || 0,
    totalSpent: balance?.total_spent || 0,
    profileCreditsGranted: balance?.profile_credits_granted || false,
    verificationCreditsGranted: balance?.verification_credits_granted || false,
    costs,
    loading,
    error,
    hasEnoughCredits,
    getCost,
    deductCreditsForService,
    refreshBalance
  };
};

export default useCredits;
